import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { trpc } from "../../app/trpc.js";
import { Badge, Button, buttonClass, Input, Notice, Sheet, Spinner } from "../../ui/index.js";
import { dayKey, fmtDay, fmtMoney, fmtTime, useCabinet } from "../cabinet/index.js";
import { invalidateBookings } from "./invalidate.js";
import { RescheduleForm } from "./RescheduleForm.js";
import { instagramLink, statusTone } from "./status.js";

type Mode = "view" | "move" | "cancel" | "reject";
const small = "px-3 py-2 text-sm";

/**
 * Запись: клиент и как связаться, услуги, сумма; подтвердить/отклонить, перенести, отменить, неявка.
 * На телефоне шторка снизу, на широком экране боковая панель справа (календарь остаётся виден).
 */
export function BookingSheet({ bookingId, onClose }: { bookingId: string | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const cab = useCabinet();
  const tz = cab.timezone;
  const [mode, setMode] = useState<Mode>("view");
  const [reason, setReason] = useState("");
  const info = useQuery(trpc.cabinet.contacts.queryOptions(bookingId ? { salonId: cab.salonId, bookingIds: [bookingId] } : skipToken));
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId, includeInactive: true }));
  const done = () => {
    invalidateBookings();
    setMode("view");
  };
  const confirm = useMutation(trpc.calendar.bookings.confirm.mutationOptions({ onSuccess: done }));
  const reject = useMutation(trpc.calendar.bookings.reject.mutationOptions({ onSuccess: done }));
  const cancel = useMutation(trpc.calendar.bookings.cancel.mutationOptions({ onSuccess: done }));
  const noShow = useMutation(trpc.calendar.bookings.markNoShow.mutationOptions({ onSuccess: done }));
  const busy = confirm.isPending || reject.isPending || cancel.isPending || noShow.isPending;
  const failed = confirm.isError || reject.isError || cancel.isError || noShow.isError;

  const close = () => {
    setMode("view");
    setReason("");
    onClose();
  };
  const row = info.data?.[0];
  const b = row?.booking;
  const total = b?.items.reduce((s, i) => s + i.price.amount, 0) ?? 0;
  const live = b && (b.status === "pending" || b.status === "confirmed");
  const started = b && b.period.start.getTime() <= Date.now();

  return (
    <Sheet open={Boolean(bookingId)} onClose={close} title={t("cal.booking")} side>
      {info.isPending && <Spinner />}
      {info.data && !row && <Notice kind="error">{t("cal.bookingNotFound")}</Notice>}
      {b && row && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xl font-semibold">
                {fmtTime(b.period.start, tz, lang)}–{fmtTime(b.period.end, tz, lang)}
              </div>
              <Badge tone={statusTone[b.status]}>{t(`cal.status.${b.status}`)}</Badge>
            </div>
            <div className="text-slate-600">{fmtDay(dayKey(b.period.start, tz), lang, { weekday: "long", day: "numeric", month: "long" })}</div>
            <div className="mt-2">{b.items.map((i) => i.name).join(" + ")}</div>
            <div className="text-sm text-slate-500">
              {masters.data?.find((m) => m.id === b.masterId)?.name} · {fmtMoney(total, lang)} · {t(`cal.source.${b.source}`)}
              {b.lateCancel && ` · ${t("cal.lateCancel")}`}
            </div>
          </div>

          <div className="rounded-2xl bg-slate-50 p-3">
            <div className="font-medium">{row.client?.name || (row.client?.instagram ? `@${row.client.instagram}` : t("cal.noClient"))}</div>
            {row.client && row.client.noShowCount > 0 && <div className="text-sm text-red-700">{t("clients.noShows", { count: row.client.noShowCount })}</div>}
            <div className="mt-2 flex flex-wrap gap-2">
              {row.conversationId && (
                <Link to={`${cab.base}/conversations/${row.conversationId}`} onClick={close} className={buttonClass("secondary", small)}>
                  {t("cal.openChat")}
                </Link>
              )}
              {row.client?.instagram && (
                <a href={instagramLink(row.client.instagram)} target="_blank" rel="noreferrer" className={buttonClass("secondary", small)}>
                  Instagram
                </a>
              )}
              {row.client?.phone && (
                <a href={`tel:${row.client.phone}`} className={buttonClass("secondary", small)}>
                  {t("cal.call")}
                </a>
              )}
              {row.client && (
                <Link to={`${cab.base}/clients/${row.client.id}`} onClick={close} className={buttonClass("ghost", small)}>
                  {t("cal.clientCard")}
                </Link>
              )}
            </div>
          </div>

          {failed && <Notice kind="error">{t("common.error")}</Notice>}

          {mode === "view" && (
            <div className="space-y-2">
              {b.status === "pending" && (
                <div className="flex gap-2">
                  <Button className="flex-1" disabled={busy} onClick={() => confirm.mutate({ bookingId: b.id })}>
                    {t("cal.confirm")}
                  </Button>
                  <Button variant="secondary" className="flex-1" disabled={busy} onClick={() => setMode("reject")}>
                    {t("cal.reject")}
                  </Button>
                </div>
              )}
              {live && (
                <div className="flex gap-2">
                  <Button variant="secondary" className="flex-1" disabled={busy} onClick={() => setMode("move")}>
                    {t("cal.move")}
                  </Button>
                  <Button variant="secondary" className="flex-1" disabled={busy} onClick={() => setMode("cancel")}>
                    {t("cal.cancel")}
                  </Button>
                </div>
              )}
              {b.status === "confirmed" && started && (
                <Button variant="ghost" className="w-full text-red-700" disabled={busy} onClick={() => noShow.mutate({ bookingId: b.id })}>
                  {t("cal.noShow")}
                </Button>
              )}
            </div>
          )}

          {mode === "move" && <RescheduleForm booking={b} onDone={done} onCancel={() => setMode("view")} />}

          {mode === "cancel" && (
            <div className="space-y-2">
              <Notice>{t("cal.cancelHint")}</Notice>
              <div className="flex gap-2">
                <Button className="flex-1 bg-red-700 hover:bg-red-800" disabled={busy} onClick={() => cancel.mutate({ bookingId: b.id })}>
                  {t("cal.cancelConfirm")}
                </Button>
                <Button variant="ghost" onClick={() => setMode("view")}>
                  {t("common.back")}
                </Button>
              </div>
            </div>
          )}

          {mode === "reject" && (
            <div className="space-y-2">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("cal.rejectReason")} />
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  disabled={busy}
                  onClick={() => reject.mutate({ bookingId: b.id, ...(reason.trim() ? { reason: reason.trim() } : {}) })}
                >
                  {t("cal.rejectConfirm")}
                </Button>
                <Button variant="ghost" onClick={() => setMode("view")}>
                  {t("common.back")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}
